// Creates a promise paired with externally callable resolve/reject that can only
// settle once. Used to model SDK lifecycle milestones as awaitable values so the
// start flow is independent of event/subscribe ordering.
import diag from '@/util/messengerDiagnostics'

function createDeferred() {
  var deferred = {}
  deferred.settled = false
  deferred.promise = new Promise(function (resolve, reject) {
    deferred._resolve = resolve
    deferred._reject = reject
  })
  deferred.resolve = function (value) {
    if (deferred.settled) return
    deferred.settled = true
    deferred._resolve(value)
  }
  deferred.reject = function (err) {
    if (deferred.settled) return
    deferred.settled = true
    deferred._reject(err)
  }
  return deferred
}

class GenesysService {
  constructor(store) {
    this.store = store
    this.ready = false

    // Coarse lifecycle state for logging/guarding:
    // IDLE → INITIALIZING → READY → SESSION_STARTED (→ ERROR)
    this.state = 'IDLE'

    // SDK lifecycle milestones, resolved by the event handlers in init().
    // Awaiting an already-fired milestone resolves immediately, so the start
    // flow no longer depends on whether an event fired before we subscribed.
    this._ready = createDeferred()
    this._sessionStarted = createDeferred()
    this._databaseReady = createDeferred()
    this._sessionStartedData = null

    // Pending sendMessageConfirmed waiters resolved when the customer echo
    // arrives via MessagingService.messagesReceived.
    this._echoWaiters = []
  }

  init() {
    this.state = 'INITIALIZING'
    if (!window.Genesys) {
      console.warn('[GenesysService] window.Genesys not found — SDK not loaded yet')
      return
    }

    var self = this

    // Messenger.ready fires when the widget shell is available, but the MessagingService
    // plugin may still be loading. Do NOT set ready or dispatch onReady here.
    window.Genesys('subscribe', 'Messenger.ready', function () {
      console.log('[GenesysService] Messenger.ready fired (widget shell ready, waiting for MessagingService plugin)')
    })

    // MessagingService.ready is the authoritative signal that the plugin is fully loaded
    // and all MessagingService.* commands (startConversation, sendMessage, etc.) can be issued.
    window.Genesys('subscribe', 'MessagingService.ready', function () {
      console.log('[GenesysService] MessagingService.ready fired — plugin loaded, setting ready=true')
      diag.log('svc', 'MessagingService.ready')
      self.ready = true
      self.state = 'READY'
      self._ready.resolve()
      self.store.dispatch('messenger/onReady')
    })

    // Database.ready gates Database.set (custom attributes). Subscribed once here so
    // the start flow can await it instead of wiring an ad-hoc subscriber each time.
    window.Genesys('subscribe', 'Database.ready', function () {
      console.log('[GenesysService] Database.ready fired')
      diag.log('svc', 'Database.ready')
      self._databaseReady.resolve()
    })

    window.Genesys('subscribe', 'MessagingService.started', function (event) {
      var data = event && event.data ? event.data : {}
      console.log('[GenesysService] MessagingService.started', data)
      diag.log('svc', 'MessagingService.started ' + JSON.stringify(data))
      // Authoritative "WebSocket session is live" signal. The start flow awaits this
      // before issuing startConversation/sendMessage so commands never run against a
      // transport that is still connecting (the production race behind lost messages).
      //
      // NOTE: the payload fields (newSession, durationSeconds, expirationDate) are NOT
      // trusted for any decision. Testing showed newSession=false even when no
      // conversation exists, and durationSeconds/expirationDate track the Guest session
      // (auth token) lifetime, not the messaging threading window. The only sources of
      // truth for an active conversation are our own DB (hasActiveConversation) and the
      // last closedConversation timestamp. Conversation restore is driven solely by the
      // MessagingService.restored event, filtered against that DB cutoff.
      self.state = 'SESSION_STARTED'
      self._sessionStartedData = data
      self._sessionStarted.resolve(data)
    })

    // On page refresh / navigation, the SDK restores an active session.
    window.Genesys('subscribe', 'MessagingService.restored', function (event) {
      var data = event && event.data ? event.data : {}
      var msgs = data.messages || []
      console.log('[GenesysService] MessagingService.restored — ' + msgs.length + ' messages in history')
      console.log('[GenesysService] restored data:', data)
      diag.log('svc', 'MessagingService.restored — ' + msgs.length + ' message(s)')
      self.store.dispatch('messenger/onSessionRestored', msgs)
    })

    window.Genesys('subscribe', 'MessagingService.conversationDisconnected', function (event) {
      var data = event && event.data ? event.data : {}
      console.log('[GenesysService] conversationDisconnected', data)
      diag.log('svc', 'conversationDisconnected ' + JSON.stringify(data))
      self.store.dispatch('messenger/onConversationDisconnected', data)
    })

    // Auth.authenticated fires BEFORE the MessagingService plugin finishes loading.
    // Setting ready here would allow startConversation to be called too early,
    // which crashes with "Could not load plugin file: messagingservice.min.js".
    window.Genesys('subscribe', 'Auth.authenticated', function () {
      console.log('[GenesysService] Auth.authenticated fired (plugin may not be loaded yet — NOT setting ready)')
    })

    // New live messages during an active conversation
    window.Genesys('subscribe', 'MessagingService.messagesReceived', function (event) {
      var messages = (event && event.data && event.data.messages) || []
      console.log('[GenesysService] messagesReceived — ' + messages.length + ' message(s)')
      diag.log('svc', 'messagesReceived — ' + messages.length + ' message(s)')
      if (messages.length) {
        self.store.dispatch('messenger/onMessages', messages)
        self._resolveEchoWaiters(messages)
      }
    })

    window.Genesys('subscribe', 'MessagingService.conversationStarted', function (event) {
      console.log('[GenesysService] conversationStarted fired', event && event.data)
      diag.log('svc', 'conversationStarted')
      self.store.dispatch('messenger/onConversationStarted')
    })

    window.Genesys('subscribe', 'MessagingService.typingReceived', function () {
      self.store.dispatch('messenger/onAgentTyping')
    })

    window.Genesys('subscribe', 'MessagingService.typingTimeout', function () {
      self.store.dispatch('messenger/onAgentTypingStop')
    })

    window.Genesys('subscribe', 'MessagingService.uploadApproved', function (event) {
      console.log('[GenesysService] uploadApproved', event && event.data)
      self.store.dispatch('messenger/onUploadApproved', event && event.data)
    })

    window.Genesys('subscribe', 'MessagingService.uploading', function (event) {
      console.log('[GenesysService] uploading', event && event.data)
      self.store.dispatch('messenger/onUploading', event && event.data)
    })

    window.Genesys('subscribe', 'MessagingService.fileUploaded', function (event) {
      console.log('[GenesysService] fileUploaded', event && event.data)
      self.store.dispatch('messenger/onFileUploaded', event && event.data)
    })

    window.Genesys('subscribe', 'MessagingService.fileUploadError', function (event) {
      console.warn('[GenesysService] fileUploadError', event && event.data)
      self.store.dispatch('messenger/onUploadError', event && event.data)
    })

    window.Genesys('subscribe', 'MessagingService.error', function (event) {
      // Generic SDK error channel. The SDK fires a benign empty error here during
      // session establishment (content-profile check / removeSessionActiveStorage
      // cleanup), so this must NOT be surfaced to the user. Real file-upload failures
      // arrive on the dedicated MessagingService.fileUploadError event (handled above),
      // which is the only place onUploadError should be dispatched from.
      console.warn('[GenesysService] MessagingService.error (logged only, not surfaced)', event && event.data)
      diag.log('svc', 'MessagingService.error ' + JSON.stringify((event && event.data) || null))
    })

    // connectionClosed fires on WebSocket drop, token expiry, or auth failure.
    window.Genesys('subscribe', 'MessagingService.connectionClosed', function (event) {
      console.log('[GenesysService] connectionClosed fired', event && event.data)
      diag.log('svc', 'connectionClosed ' + JSON.stringify((event && event.data) || null))
      self.store.dispatch('messenger/onConnectionClosed')
    })

    // Auth failures (e.g. unsupported_grant_type after user switch without page reload).
    // Guard with self.ready: the SDK fires Auth.error during startup while trying to
    // refresh a stale implicit-flow token — that is a normal part of initialisation
    // and must not surface as an auth failure to the user.
    window.Genesys('subscribe', 'Auth.error', function (event) {
      if (!self.ready) {
        console.log('[GenesysService] Auth.error fired before ready (startup token refresh) — ignoring')
        return
      }
      var data = event && event.data ? event.data : {}
      if (data.error === 'unsupported_grant_type' && data.authGrantType === 'implicit') {
        console.log('[GenesysService] Auth.error unsupported_grant_type for implicit flow — ignoring')
        return
      }
      console.warn('[GenesysService] Auth.error fired', event && event.data)
      diag.log('svc', 'Auth.error ' + JSON.stringify(data))
      self.store.dispatch('messenger/onAuthError')
    })

    // Also catch the implicit-flow refresh error that surfaces as a plain error object.
    // Same guard: only act after the SDK has confirmed ready.
    var origConsoleError = console.error
    console.error = function () {
      var args = Array.prototype.slice.call(arguments)
      var msg = args[0]
      if (msg && typeof msg === 'object' && msg.error === 'unsupported_grant_type') {
        if (!self.ready) {
          origConsoleError.apply(console, args)
          return
        }
        if (msg.authGrantType === 'implicit') {
          console.log('[GenesysService] unsupported_grant_type for implicit flow observed in console.error — ignoring')
          origConsoleError.apply(console, args)
          return
        }
        console.warn('[GenesysService] Caught unsupported_grant_type in console.error — dispatching onAuthError')
        self.store.dispatch('messenger/onAuthError')
      }
      origConsoleError.apply(console, args)
    }
  }

  resetSession() {
    // Clear the SDK's own auth storage so the next login cycle gets a fresh token
    try {
      window.Genesys('command', 'Auth.logout', {},
        function () { console.log('[GenesysService] Auth.logout succeeded') },
        function (err) { console.warn('[GenesysService] Auth.logout failed (expected for implicit flow)', err) }
      )
    } catch (e) {
      console.warn('[GenesysService] Auth.logout threw', e)
    }

    // Purge any Genesys keys from localStorage so stale tokens can't survive a user switch
    try {
      var keysToRemove = []
      for (var i = 0; i < localStorage.length; i++) {
        var key = localStorage.key(i)
        if (key && (key.indexOf('genesys') !== -1 || key.indexOf('cx_') !== -1 || key.indexOf('_genesys') !== -1)) {
          keysToRemove.push(key)
        }
      }
      keysToRemove.forEach(function (k) { localStorage.removeItem(k) })
      if (keysToRemove.length) {
        console.log('[GenesysService] Cleared', keysToRemove.length, 'Genesys localStorage keys')
      }
    } catch (e) {
      console.warn('[GenesysService] localStorage cleanup failed', e)
    }
  }

  startConversation() {
    console.log('[GenesysService] startConversation — issuing MessagingService.startConversation command')
    diag.log('svc', 'startConversation — command issued')
    return new Promise(function (resolve, reject) {
      window.Genesys('command', 'MessagingService.startConversation', {},
        function (res) {
          console.log('[GenesysService] startConversation succeeded (new conversation opened)', res)
          diag.log('svc', 'startConversation — succeeded (new conversation)')
          resolve(res)
        },
        function (err) {
          // Genesys rejects with "There is already an active conversation." when a
          // conversation from a previous page session was restored before we called
          // this. (With "auto start conversation" disabled the SDK no longer opens one
          // on its own, so this now only happens for genuinely restored conversations.)
          // This is not an error — we simply join the already-open conversation.
          if (err && typeof err === 'string' && err.indexOf('already an active conversation') !== -1) {
            console.log('[GenesysService] startConversation — an existing conversation was restored, joining it')
            diag.log('svc', 'startConversation — already active, joining existing')
            resolve('joined')
          } else {
            console.warn('[GenesysService] startConversation rejected by SDK', err)
            diag.log('svc', 'startConversation \u2014 rejected: ' + JSON.stringify(err))
            reject(err)
          }
        }
      )
    })
  }

  // ── Lifecycle milestones ────────────────────────────────────────────────

  // Resolves when MessagingService.ready has fired (plugin loaded, commands issuable).
  whenReady() {
    return this._ready.promise
  }

  // Resolves with the MessagingService.started payload ({ newSession, authenticated, ... }).
  // This is the gate that guarantees the WebSocket session is live before we issue
  // startConversation/sendMessage.
  whenSessionStarted() {
    return this._sessionStarted.promise
  }

  // Writes conversation custom attributes to the Database once Database.ready has
  // fired. These MUST be set before startConversation: the SDK captures whatever is
  // in the Database at the JOIN event (conversation creation), so the conversation
  // is created with these attributes attached for agent-side routing.
  setCustomAttributes(attrs) {
    return this._databaseReady.promise.then(function () {
      return new Promise(function (resolve) {
        // Resolve on both success and failure callbacks: a Database.set failure must
        // not block conversation start (attributes are best-effort metadata).
        window.Genesys('command', 'Database.set', { messaging: { customAttributes: attrs } }, resolve, resolve)
      })
    })
  }

  // ── Confirmed message send ──────────────────────────────────────────────

  // Returns true if the message echoed back to the customer (Inbound) matches `text`.
  _isCustomerEcho(message, text) {
    if (!message) return false
    var dir = message.direction || message.messageType
    var isInbound =
      dir === 'Inbound' ||
      dir === 'inbound' ||
      (message.from && message.from.role === 'Customer') ||
      message.originatingEntity === 'Human'
    if (!isInbound) return false
    var body = message.text || (message.content && message.content[0] && message.content[0].statement) || ''
    return body.trim() === String(text).trim()
  }

  // Called from the messagesReceived handler to settle any pending confirmed sends.
  _resolveEchoWaiters(messages) {
    if (!this._echoWaiters.length) return
    var self = this
    this._echoWaiters = this._echoWaiters.filter(function (waiter) {
      var matched = messages.some(function (m) { return self._isCustomerEcho(m, waiter.text) })
      if (matched) {
        waiter.resolve(true)
        return false
      }
      return true
    })
  }

  // Sends a message and resolves once the SDK echoes it back to the customer,
  // confirming the server accepted it. If no echo arrives within timeoutMs the
  // message is re-sent up to `retries` times before resolving false (sent but
  // unconfirmed). Rejects only if the sendMessage command itself errors.
  sendMessageConfirmed(text, options) {
    var self = this
    options = options || {}
    var timeoutMs = typeof options.timeoutMs === 'number' ? options.timeoutMs : 3000
    var maxAttempts = (typeof options.retries === 'number' ? options.retries : 1) + 1

    function sendOnce() {
      return new Promise(function (resolve, reject) {
        window.Genesys('command', 'MessagingService.sendMessage', { message: text },
          function (res) { resolve(res) },
          function (err) {
            diag.log('svc', 'sendMessageConfirmed \u2014 sendMessage command rejected: ' + JSON.stringify(err))
            reject(err)
          }
        )
      })
    }

    function waitForEcho() {
      return new Promise(function (resolve) {
        var waiter = { text: text, resolve: null }
        var timer = setTimeout(function () {
          self._echoWaiters = self._echoWaiters.filter(function (w) { return w !== waiter })
          resolve(false)
        }, timeoutMs)
        waiter.resolve = function (val) {
          clearTimeout(timer)
          resolve(val)
        }
        self._echoWaiters.push(waiter)
      })
    }

    function attempt(n) {
      // Register the echo waiter before sending so a fast echo cannot be missed.
      var echo = waitForEcho()
      diag.log('svc', 'sendMessageConfirmed \u2014 attempt ' + (n + 1) + '/' + maxAttempts)
      return sendOnce()
        .then(function () {
          diag.log('svc', 'sendMessageConfirmed \u2014 command accepted (attempt ' + (n + 1) + '), awaiting echo')
          return echo
        })
        .then(function (confirmed) {
          if (confirmed) {
            console.log('[GenesysService] sendMessageConfirmed — echo confirmed (attempt ' + (n + 1) + ')')
            diag.log('svc', 'sendMessageConfirmed \u2014 echo confirmed (attempt ' + (n + 1) + ')')
            return true
          }
          if (n + 1 < maxAttempts) {
            console.warn('[GenesysService] sendMessageConfirmed — no echo, retrying (attempt ' + (n + 2) + ')')
            diag.log('svc', 'sendMessageConfirmed \u2014 no echo within ' + timeoutMs + 'ms, retrying')
            return attempt(n + 1)
          }
          console.warn('[GenesysService] sendMessageConfirmed — sent but unconfirmed after ' + maxAttempts + ' attempt(s)')
          diag.log('svc', 'sendMessageConfirmed \u2014 UNCONFIRMED after ' + maxAttempts + ' attempt(s)')
          return false
        })
    }

    console.log('[GenesysService] sendMessageConfirmed — "' + text + '"')
    return attempt(0)
  }

  sendTyping() {
    window.Genesys('command', 'MessagingService.sendTyping')
  }

  requestUpload(file) {
    var fileList = null

    if (file && typeof file.length === 'number' && file.item && file.length > 0) {
      fileList = file
    } else if (file && file.name) {
      // Messenger SDK expects HTML5 FileList (single file), not a raw File.
      try {
        var dt = new DataTransfer()
        dt.items.add(file)
        fileList = dt.files
      } catch (e) {
        console.warn('[GenesysService] Failed to convert File to FileList for requestUpload', e)
      }
    }

    var firstFile = fileList && fileList.length ? fileList[0] : null
    if (!firstFile || !firstFile.name) {
      var badFileErr = new Error('Invalid file payload for requestUpload')
      console.warn('[GenesysService] requestUpload called with invalid file', file)
      return Promise.reject(badFileErr)
    }

    if (!this.ready) {
      console.warn('[GenesysService] requestUpload called before MessagingService.ready')
    }

    console.log('[GenesysService] requestUpload — ' + firstFile.name)
    return new Promise(function (resolve, reject) {
      window.Genesys('command', 'MessagingService.requestUpload', { file: fileList },
        function (res) {
          console.log('[GenesysService] requestUpload accepted', res)
          resolve(res)
        },
        function (err) {
          console.warn('[GenesysService] requestUpload rejected', err)
          reject(err)
        }
      )
    })
  }

  sendMessage(text) {
    console.log('[GenesysService] sendMessage — "' + text + '"')
    return new Promise(function (resolve, reject) {
      window.Genesys('command', 'MessagingService.sendMessage', { message: text },
        function (res) {
          console.log('[GenesysService] sendMessage succeeded', res)
          resolve(res)
        },
        function (err) {
          console.warn('[GenesysService] sendMessage failed', err)
          reject(err)
        }
      )
    })
  }

  disconnectConversation() {
    console.log('[GenesysService] disconnectConversation — closing conversation')
    return new Promise(function (resolve, reject) {
      window.Genesys('command', 'MessagingService.closeConversation', {},
        function (res) {
          console.log('[GenesysService] disconnectConversation succeeded', res)
          resolve(res)
        },
        function (err) {
          console.warn('[GenesysService] disconnectConversation failed', err)
          reject(err)
        }
      )
    })
  }
}

export default GenesysService
